import { Transition } from '@headlessui/react';
import { ChevronRightIcon } from '@heroicons/react/outline';
import classNames from 'classnames';

import Avatar from '../../components/Avatar';
import { pluralize } from '../../lib/copy';
import { PractitionerAssignmentsQuery } from '../../../generated/graphql';

import ClientTable from './ClientTable';
import type { ClientsDateRange } from './insightsFilters';

type PractitionerAssignment =
  PractitionerAssignmentsQuery['practitionerAssignments'][number];

interface PractitionerCardProps {
  delay: number;
  practitioner: PractitionerAssignment['practitioner'];
  clients: PractitionerAssignment['assignedClients'];
  dateRange: ClientsDateRange | null;
  isExpanded: boolean;
  onToggleExpanded: () => void;
}

const PractitionerCard: React.FC<PractitionerCardProps> = ({
  practitioner,
  clients,
  delay,
  dateRange,
  isExpanded,
  onToggleExpanded,
}) => (
  <Transition
    className="mb-10 flex w-full flex-row justify-center"
    appear={true}
    show={true}
    enter="transform transition duration-500"
    enterFrom="opacity-0 -translate-y-5"
    enterTo="opacity-100 translate-y-0"
    style={{ transitionDelay: `${delay}s` }}
  >
    <div className="flex w-full flex-col overflow-x-auto rounded-lg border border-neutral-75">
      <div className="flex h-2 w-full rounded-t-lg bg-secondary-125"></div>
      <button
        type="button"
        onClick={onToggleExpanded}
        aria-expanded={isExpanded}
        className="m-4 flex w-auto flex-row items-center justify-between text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary-50"
      >
        <div className="flex flex-row items-center">
          <ChevronRightIcon
            className={classNames(
              'mr-2 h-5 w-5 shrink-0 text-neutral-125 transition-transform duration-200',
              { 'rotate-90': isExpanded },
            )}
          />
          <Avatar
            size="medium-large"
            name={practitioner.name}
            imageUrl={practitioner.profileImageUrl}
            className="mr-3"
          />
          <div className="flex flex-col">
            <div className="caption font-sans font-medium text-green-150">
              {practitioner.name}
            </div>
            <div className="font-sans text-caption text-neutral-110">
              {practitioner.role}
              {' · '}
              {pluralize(clients.length, 'active client', 'active clients')}
            </div>
          </div>
        </div>
      </button>
      {isExpanded && (
        <ClientTable
          clients={clients}
          practitionerId={practitioner.id}
          dateRange={dateRange}
        />
      )}
    </div>
  </Transition>
);

export default PractitionerCard;
